/**
 * SENS Nebula Orb — lightweight WebGL particle sphere with pointer hover.
 * Used by the cyber chat theme (launcher + idle messages backdrop).
 */
(function (global) {
  "use strict";

  var VERT = [
    "attribute vec3 aPosition;",
    "attribute float aSeed;",
    "uniform mat4 uProjection;",
    "uniform mat4 uView;",
    "uniform vec2 uPointer;",
    "uniform vec2 uPointerVel;",
    "uniform float uHover;",
    "uniform float uSpeedFactor;",
    "uniform float uElastic;",
    "uniform float uPointScale;",
    "uniform float uHoverStrength;",
    "uniform float uBlobFalloff;",
    "uniform float uPushStrength;",
    "uniform float uPushJitter;",
    "uniform float uSmudgeStrength;",
    "uniform float uRippleStrength;",
    "uniform float uRippleSpeed;",
    "uniform float uSwirlStrength;",
    "uniform float uSwirlSpeed;",
    "uniform float uNoiseFreq;",
    "uniform float uNoiseAmp;",
    "uniform float uNoiseSpeed;",
    "uniform float uTime;",
    "varying float vElev;",
    "varying float vPulse;",
    "vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }",
    "vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }",
    "vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }",
    "vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }",
    "float snoise(vec3 v) {",
    "  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);",
    "  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);",
    "  vec3 i  = floor(v + dot(v, C.yyy));",
    "  vec3 x0 = v - i + dot(i, C.xxx);",
    "  vec3 g = step(x0.yzx, x0.xyz);",
    "  vec3 l = 1.0 - g;",
    "  vec3 i1 = min(g.xyz, l.zxy);",
    "  vec3 i2 = max(g.xyz, l.zxy);",
    "  vec3 x1 = x0 - i1 + C.xxx;",
    "  vec3 x2 = x0 - i2 + C.yyy;",
    "  vec3 x3 = x0 - D.yyy;",
    "  i = mod289(i);",
    "  vec4 p = permute(permute(permute(",
    "    i.z + vec4(0.0, i1.z, i2.z, 1.0))",
    "    + i.y + vec4(0.0, i1.y, i2.y, 1.0))",
    "    + i.x + vec4(0.0, i1.x, i2.x, 1.0));",
    "  float n_ = 0.142857142857;",
    "  vec3 ns = n_ * D.wyz - D.xzx;",
    "  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);",
    "  vec4 x_ = floor(j * ns.z);",
    "  vec4 y_ = floor(j - 7.0 * x_);",
    "  vec4 x = x_ * ns.x + ns.yyyy;",
    "  vec4 y = y_ * ns.x + ns.yyyy;",
    "  vec4 h = 1.0 - abs(x) - abs(y);",
    "  vec4 b0 = vec4(x.xy, y.xy);",
    "  vec4 b1 = vec4(x.zw, y.zw);",
    "  vec4 s0 = floor(b0) * 2.0 + 1.0;",
    "  vec4 s1 = floor(b1) * 2.0 + 1.0;",
    "  vec4 sh = -step(h, vec4(0.0));",
    "  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;",
    "  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;",
    "  vec3 p0 = vec3(a0.xy, h.x);",
    "  vec3 p1 = vec3(a0.zw, h.y);",
    "  vec3 p2 = vec3(a1.xy, h.z);",
    "  vec3 p3 = vec3(a1.zw, h.w);",
    "  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));",
    "  p0 *= norm.x;",
    "  p1 *= norm.y;",
    "  p2 *= norm.z;",
    "  p3 *= norm.w;",
    "  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);",
    "  m = m * m;",
    "  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));",
    "}",
    "void main() {",
    "  vec3 pos = aPosition;",
    "  vec3 n = normalize(pos);",
    "  float breathe = snoise(vec3(n.x * uNoiseFreq, n.y * uNoiseFreq, n.z * uNoiseFreq + uTime * uNoiseSpeed));",
    "  pos += n * breathe * uNoiseAmp;",
    "  vec2 plane = vec2(",
    "    dot(pos, vec3(1.0, 0.0, 0.2)),",
    "    dot(pos, vec3(0.0, 1.0, 0.15))",
    "  );",
    "  float infl = exp(-dot(plane - uPointer * 1.05, plane - uPointer * 1.05) * uBlobFalloff);",
    "  float speedBoost = 1.0 + uSpeedFactor * 2.6 + max(0.0, uElastic) * 0.95;",
    "  float h = infl * uHover * uHoverStrength * (0.5 + speedBoost * 0.5);",
    "  vec2 toPointer = uPointer - plane;",
    "  float push = h * (uPushStrength + aSeed * uPushJitter) * speedBoost;",
    "  push += uElastic * h * (0.16 + aSeed * 0.07);",
    "  vec3 tangent = normalize(cross(n, vec3(0.02, 1.0, 0.0)));",
    "  vec3 bitangent = cross(n, tangent);",
    "  vec2 velN = uPointerVel;",
    "  float velLen = length(velN);",
    "  if (velLen > 0.0001) velN /= velLen;",
    "  float sweep = uSpeedFactor * 0.36;",
    "  pos += n * push;",
    "  pos += tangent * h * (toPointer.x * uSmudgeStrength + velN.x * sweep);",
    "  pos += bitangent * h * (toPointer.y * uSmudgeStrength + velN.y * sweep);",
    "  float bounceWave = sin(uTime * (7.0 + uSpeedFactor * 6.0) + aSeed * 18.0 + length(plane - uPointer) * 4.5);",
    "  pos += n * h * uElastic * bounceWave * (0.13 + uSpeedFactor * 0.09);",
    "  pos += tangent * h * uSwirlStrength * (1.0 + uSpeedFactor * 1.4) * sin(uTime * (uSwirlSpeed + uSpeedFactor * 2.4) + aSeed * 14.0);",
    "  pos += bitangent * h * (uSwirlStrength * 0.82) * (1.0 + uSpeedFactor) * cos(uTime * ((uSwirlSpeed * 0.86) + uSpeedFactor * 2.0) + aSeed * 11.0);",
    "  pos += n * h * uRippleStrength * (1.0 + uSpeedFactor * 1.35) * sin(uTime * (uRippleSpeed + uSpeedFactor * 4.0) + length(plane - uPointer) * 5.0);",
    "  vElev = n.y;",
    "  vPulse = 1.0 + h * (3.1 + uSpeedFactor * 2.4 + max(0.0, uElastic) * 1.8);",
    "  vec4 mv = uView * vec4(pos, 1.0);",
    "  gl_Position = uProjection * mv;",
    "  float size = (uPointScale + aSeed * uPointScale * 0.35) * (165.0 / max(0.35, -mv.z));",
    "  gl_PointSize = clamp(size, 0.55, 3.8);",
    "}",
  ].join("\n");

  var FRAG = [
    "precision mediump float;",
    "varying float vElev;",
    "varying float vPulse;",
    "uniform vec3 uColorHi;",
    "uniform vec3 uColorLo;",
    "uniform vec3 uColorAccent;",
    "void main() {",
    "  vec2 uv = gl_PointCoord - 0.5;",
    "  float d = length(uv);",
    "  if (d > 0.5) discard;",
      "  float alpha = smoothstep(0.5, 0.22, d);",
    "  float band = smoothstep(-0.15, 0.85, vElev);",
    "  vec3 col = mix(uColorLo, uColorHi, band);",
    "  col = mix(col, uColorAccent, smoothstep(0.72, 1.0, vElev) * 0.22);",
    "  col *= vPulse;",
    "  gl_FragColor = vec4(col, alpha * 0.92);",
    "}",
  ].join("\n");

  function prefersReducedMotion() {
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
      return false;
    }
  }

  function compileShader(gl, type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      var err = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(err || "Shader compile failed");
    }
    return shader;
  }

  function createProgram(gl, vertSrc, fragSrc) {
    var program = gl.createProgram();
    var vs = compileShader(gl, gl.VERTEX_SHADER, vertSrc);
    var fs = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc);
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      var err = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(err || "Program link failed");
    }
    return program;
  }

  function fibonacciSphere(count, radius) {
    var positions = new Float32Array(count * 3);
    var seeds = new Float32Array(count);
    var golden = Math.PI * (3 - Math.sqrt(5));
    for (var i = 0; i < count; i++) {
      var y = 1 - (i / Math.max(1, count - 1)) * 2;
      var r = Math.sqrt(Math.max(0, 1 - y * y));
      var theta = golden * i;
      var x = Math.cos(theta) * r;
      var z = Math.sin(theta) * r;
      positions[i * 3] = x * radius;
      positions[i * 3 + 1] = y * radius;
      positions[i * 3 + 2] = z * radius;
      seeds[i] = Math.random();
    }
    return { positions: positions, seeds: seeds };
  }

  function hexToRgb(hex) {
    var h = String(hex || "").replace("#", "");
    if (h.length === 3) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    }
    var n = parseInt(h, 16);
    if (isNaN(n)) return [0, 0.94, 1];
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  function mountNebulaOrb(canvas, options) {
    options = options || {};
    if (!canvas || prefersReducedMotion()) {
      return { destroy: function () {}, resize: function () {} };
    }

    var gl =
      canvas.getContext("webgl", { alpha: true, antialias: true, powerPreference: "low-power" }) ||
      canvas.getContext("experimental-webgl", { alpha: true });
    if (!gl) {
      return { destroy: function () {}, resize: function () {} };
    }

    var count = options.particleCount || 3200;
    var radius = options.radius || 1;
    var pointScale = options.pointScale || 2.4;
    var colorHi = hexToRgb(options.colorHi || "#00f0ff");
    var colorLo = hexToRgb(options.colorLo || "#0a1a33");
    var colorAccent = hexToRgb(options.colorAccent || "#ff2d95");
    var rotateSpeed = options.rotateSpeed == null ? 0.22 : options.rotateSpeed;
    var staticMode = !!options.staticMode;
    var tiltAngle = options.tiltAngle == null ? 0.28 : options.tiltAngle;
    var hoverStrength = options.hoverStrength == null ? 1 : options.hoverStrength;
    var blobFalloff = options.blobFalloff == null ? 2.4 : options.blobFalloff;
    var pushStrength = options.pushStrength == null ? 0.11 : options.pushStrength;
    var pushJitter = options.pushJitter == null ? 0.045 : options.pushJitter;
    var smudgeStrength = options.smudgeStrength == null ? 0.14 : options.smudgeStrength;
    var rippleStrength = options.rippleStrength == null ? 0.025 : options.rippleStrength;
    var rippleSpeed = options.rippleSpeed == null ? 2.2 : options.rippleSpeed;
    var swirlStrength = options.swirlStrength == null ? 0.05 : options.swirlStrength;
    var swirlSpeed = options.swirlSpeed == null ? 1.8 : options.swirlSpeed;
    var velocityMax = options.velocityMax == null ? 8.5 : options.velocityMax;
    var velocitySmoothing = options.velocitySmoothing == null ? 0.4 : options.velocitySmoothing;
    var elasticStiffness = options.elasticStiffness == null ? 240 : options.elasticStiffness;
    var elasticDamping = options.elasticDamping == null ? 10.5 : options.elasticDamping;
    var elasticGain = options.elasticGain == null ? 1.15 : options.elasticGain;
    var hoverAnimSlowdown = options.hoverAnimSlowdown == null ? 0.38 : options.hoverAnimSlowdown;
    var noiseFreq = options.noiseFreq == null ? 2.4 : options.noiseFreq;
    var noiseAmp = options.noiseAmp == null ? 0.045 : options.noiseAmp;
    var noiseSpeed = options.noiseSpeed == null ? 0.35 : options.noiseSpeed;
    var mouseTiltMax = options.mouseTiltMax == null ? 0.34 : options.mouseTiltMax;
    var mouseTiltLerp = options.mouseTiltLerp == null ? 0.05 : options.mouseTiltLerp;
    var additiveBlending = options.additiveBlending !== false;
    var cameraDistance = options.cameraDistance == null ? 3.4 : options.cameraDistance;
    var lookAtY = options.lookAtY == null ? 0 : options.lookAtY;
    var sphereOffsetY = options.sphereOffsetY == null ? 0 : options.sphereOffsetY;
    var interactionEl = options.interactionElement || canvas;

    var sphere = fibonacciSphere(count, radius);
    var program = createProgram(gl, VERT, FRAG);

    var aPosition = gl.getAttribLocation(program, "aPosition");
    var aSeed = gl.getAttribLocation(program, "aSeed");
    var uProjection = gl.getUniformLocation(program, "uProjection");
    var uView = gl.getUniformLocation(program, "uView");
    var uPointer = gl.getUniformLocation(program, "uPointer");
    var uPointerVel = gl.getUniformLocation(program, "uPointerVel");
    var uHover = gl.getUniformLocation(program, "uHover");
    var uSpeedFactor = gl.getUniformLocation(program, "uSpeedFactor");
    var uElastic = gl.getUniformLocation(program, "uElastic");
    var uHoverStrength = gl.getUniformLocation(program, "uHoverStrength");
    var uBlobFalloff = gl.getUniformLocation(program, "uBlobFalloff");
    var uPushStrength = gl.getUniformLocation(program, "uPushStrength");
    var uPushJitter = gl.getUniformLocation(program, "uPushJitter");
    var uSmudgeStrength = gl.getUniformLocation(program, "uSmudgeStrength");
    var uRippleStrength = gl.getUniformLocation(program, "uRippleStrength");
    var uRippleSpeed = gl.getUniformLocation(program, "uRippleSpeed");
    var uSwirlStrength = gl.getUniformLocation(program, "uSwirlStrength");
    var uSwirlSpeed = gl.getUniformLocation(program, "uSwirlSpeed");
    var uNoiseFreq = gl.getUniformLocation(program, "uNoiseFreq");
    var uNoiseAmp = gl.getUniformLocation(program, "uNoiseAmp");
    var uNoiseSpeed = gl.getUniformLocation(program, "uNoiseSpeed");
    var uTime = gl.getUniformLocation(program, "uTime");
    var uPointScale = gl.getUniformLocation(program, "uPointScale");
    var uColorHi = gl.getUniformLocation(program, "uColorHi");
    var uColorLo = gl.getUniformLocation(program, "uColorLo");
    var uColorAccent = gl.getUniformLocation(program, "uColorAccent");

    var posBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, sphere.positions, gl.STATIC_DRAW);

    var seedBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, seedBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, sphere.seeds, gl.STATIC_DRAW);

    gl.enable(gl.BLEND);
    if (additiveBlending) {
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    } else {
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    }
    gl.clearColor(0, 0, 0, 0);

    var pointer = { x: 0, y: 0 };
    var pointerTarget = { x: 0, y: 0 };
    var pointerRaw = { x: 0, y: 0 };
    var pointerVelocity = { x: 0, y: 0 };
    var pointerVelDisplay = { x: 0, y: 0 };
    var speedTarget = 0;
    var speedCurrent = 0;
    var elastic = 0;
    var elasticVelocity = 0;
    var hoverTarget = 0;
    var hoverCurrent = 0;
    var pointerSmooth = options.pointerSmooth == null ? 0.14 : options.pointerSmooth;
    var hoverSmooth = options.hoverSmooth == null ? 0.1 : options.hoverSmooth;
    var hoverSmoothOut = options.hoverSmoothOut == null ? hoverSmooth * 0.4 : options.hoverSmoothOut;
    var staleSampleMs = options.staleSampleMs == null ? 200 : options.staleSampleMs;
    var pointerReturnDecay = options.pointerReturnDecay == null ? 0.94 : options.pointerReturnDecay;
    var velocityMomentumDecay = options.velocityMomentumDecay == null ? 0.84 : options.velocityMomentumDecay;
    var speedMomentumDecay = options.speedMomentumDecay == null ? 0.88 : options.speedMomentumDecay;
    var lastMoveMs = 0;
    var lastFrameMs = 0;
    var pointerInitialized = false;
    var animTime = 0;
    var rotation = 0;
    var tiltInertiaX = 0;
    var tiltInertiaY = 0;
    var rafId = 0;
    var running = true;
    var dpr = 1;
    var width = 0;
    var height = 0;

    function setPointerFromEvent(event) {
      var mapEl = interactionEl || canvas;
      var rect = mapEl.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var clientX = event.clientX;
      var clientY = event.clientY;
      if (event.touches && event.touches[0]) {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      }
      var nextX = ((clientX - rect.left) / rect.width) * 2 - 1;
      var nextY = -(((clientY - rect.top) / rect.height) * 2 - 1);
      var now = performance.now();
      var elapsedMs = lastMoveMs > 0 ? now - lastMoveMs : 0;
      var isFreshSample = !pointerInitialized || lastMoveMs <= 0 || elapsedMs > staleSampleMs;

      if (!isFreshSample) {
        var dt = Math.max(0.001, Math.min(0.05, elapsedMs / 1000));
        var rawVx = (nextX - pointerRaw.x) / dt;
        var rawVy = (nextY - pointerRaw.y) / dt;
        pointerVelocity.x += (rawVx - pointerVelocity.x) * velocitySmoothing;
        pointerVelocity.y += (rawVy - pointerVelocity.y) * velocitySmoothing;
        var speed = Math.hypot(pointerVelocity.x, pointerVelocity.y);
        speedTarget = Math.min(1.45, speed / velocityMax);
        if (speed > 0.02) {
          pointerVelDisplay.x = pointerVelocity.x / speed;
          pointerVelDisplay.y = pointerVelocity.y / speed;
        }
      } else {
        // Re-entering after a gap (or first-ever sample): reposition only, never
        // synthesize a velocity spike from the stale distance — that's what caused
        // the "sudden blast" the moment the cursor entered the header.
        pointerRaw.x = nextX;
        pointerRaw.y = nextY;
        pointerInitialized = true;
      }

      pointerTarget.x = nextX;
      pointerTarget.y = nextY;
      pointerRaw.x = nextX;
      pointerRaw.y = nextY;
      lastMoveMs = now;
      hoverTarget = 1;
    }

    function decayPointerMotion(dtSec) {
      var idleSec = (performance.now() - lastMoveMs) / 1000;
      if (idleSec > 0.05) {
        var decay = Math.pow(velocityMomentumDecay, dtSec * 60);
        pointerVelocity.x *= decay;
        pointerVelocity.y *= decay;
        speedTarget *= Math.pow(speedMomentumDecay, dtSec * 60);
      }
    }

    function stepElasticPhysics(dtSec) {
      decayPointerMotion(dtSec);
      speedCurrent += (speedTarget - speedCurrent) * Math.min(1, 16 * dtSec);
      var elasticTarget = speedCurrent * elasticGain;
      var accel = (elasticTarget - elastic) * elasticStiffness - elasticVelocity * elasticDamping;
      elasticVelocity += accel * dtSec;
      elastic += elasticVelocity * dtSec;
      if (elastic > 1.65) {
        elastic = 1.65;
        elasticVelocity *= 0.35;
      } else if (elastic < -0.22) {
        elastic = -0.22;
        elasticVelocity *= 0.25;
      }
    }

    function onPointerMove(event) {
      setPointerFromEvent(event);
    }

    function onPointerLeave() {
      // Intentionally leave speedTarget/pointerVelocity alone — decayPointerMotion()
      // fades them out gradually so the swirl keeps carrying its momentum for a
      // moment after the cursor leaves instead of snapping to a dead stop.
      hoverTarget = 0;
    }

    function onPointerEnter(event) {
      setPointerFromEvent(event);
    }

    function bindPointerEvents(el) {
      if (!el) return;
      el.addEventListener("pointermove", onPointerMove, { passive: true });
      el.addEventListener("pointerenter", onPointerEnter, { passive: true });
      el.addEventListener("pointerleave", onPointerLeave, { passive: true });
      el.addEventListener("touchmove", onPointerMove, { passive: true });
    }

    bindPointerEvents(canvas);
    if (interactionEl !== canvas) bindPointerEvents(interactionEl);

    var pointerTargets = [canvas];
    if (interactionEl !== canvas) pointerTargets.push(interactionEl);

    function perspective(fov, aspect, near, far) {
      var f = 1 / Math.tan(fov / 2);
      var nf = 1 / (near - far);
      return new Float32Array([
        f / aspect, 0, 0, 0,
        0, f, 0, 0,
        0, 0, (far + near) * nf, -1,
        0, 0, 2 * far * near * nf, 0,
      ]);
    }

    function lookAt(eye, center, up) {
      var zx = eye[0] - center[0];
      var zy = eye[1] - center[1];
      var zz = eye[2] - center[2];
      var zLen = Math.hypot(zx, zy, zz) || 1;
      zx /= zLen;
      zy /= zLen;
      zz /= zLen;
      var xx = up[1] * zz - up[2] * zy;
      var xy = up[2] * zx - up[0] * zz;
      var xz = up[0] * zy - up[1] * zx;
      var xLen = Math.hypot(xx, xy, xz) || 1;
      xx /= xLen;
      xy /= xLen;
      xz /= xLen;
      var yx = zy * xz - zz * xy;
      var yy = zz * xx - zx * xz;
      var yz = zx * xy - zy * xx;
      return new Float32Array([
        xx, yx, zx, 0,
        xy, yy, zy, 0,
        xz, yz, zz, 0,
        -(xx * eye[0] + xy * eye[1] + xz * eye[2]),
        -(yx * eye[0] + yy * eye[1] + yz * eye[2]),
        -(zx * eye[0] + zy * eye[1] + zz * eye[2]),
        1,
      ]);
    }

    function multiply(a, b) {
      var out = new Float32Array(16);
      for (var i = 0; i < 4; i++) {
        for (var j = 0; j < 4; j++) {
          out[i * 4 + j] =
            a[i * 4] * b[j] +
            a[i * 4 + 1] * b[4 + j] +
            a[i * 4 + 2] * b[8 + j] +
            a[i * 4 + 3] * b[12 + j];
        }
      }
      return out;
    }

    function rotateY(angle) {
      var c = Math.cos(angle);
      var s = Math.sin(angle);
      return new Float32Array([
        c, 0, s, 0,
        0, 1, 0, 0,
        -s, 0, c, 0,
        0, 0, 0, 1,
      ]);
    }

    function rotateX(angle) {
      var c = Math.cos(angle);
      var s = Math.sin(angle);
      return new Float32Array([
        1, 0, 0, 0,
        0, c, -s, 0,
        0, s, c, 0,
        0, 0, 0, 1,
      ]);
    }

    function translateY(y) {
      return new Float32Array([
        1, 0, 0, 0,
        0, 1, 0, 0,
        0, 0, 1, 0,
        0, y, 0, 1,
      ]);
    }

    function resize() {
      var rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = Math.max(1, Math.round(rect.width * dpr));
      height = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    }

    function draw(time) {
      if (!running) return;
      rafId = global.requestAnimationFrame(draw);

      resize();
      var dtSec = lastFrameMs ? Math.min(0.04, (time - lastFrameMs) / 1000) : 0.016;
      lastFrameMs = time;
      stepElasticPhysics(dtSec);

      var pointerLerp = hoverTarget > 0.01 ? pointerSmooth : pointerSmooth * 0.55;
      pointer.x += (pointerTarget.x - pointer.x) * pointerLerp;
      pointer.y += (pointerTarget.y - pointer.y) * pointerLerp;
      if (hoverTarget < 0.01) {
        var returnDecay = Math.pow(pointerReturnDecay, dtSec * 60);
        pointerTarget.x *= returnDecay;
        pointerTarget.y *= returnDecay;
      }
      var hoverRate = hoverTarget > hoverCurrent ? hoverSmooth : hoverSmoothOut;
      hoverCurrent += (hoverTarget - hoverCurrent) * hoverRate;
      var animRate = 1 - hoverCurrent * (1 - hoverAnimSlowdown);
      animTime += dtSec * animRate;
      if (!staticMode) {
        rotation += rotateSpeed * 0.0038 * animRate;
      }

      var tiltTargetX = pointer.y * mouseTiltMax;
      var tiltTargetY = pointer.x * mouseTiltMax;
      tiltInertiaX += (tiltTargetX - tiltInertiaX) * mouseTiltLerp;
      tiltInertiaY += (tiltTargetY - tiltInertiaY) * mouseTiltLerp;

      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);

      var aspect = width / height;
      var projection = perspective(Math.PI / 3.2, aspect, 0.1, 100);
      var view = lookAt([0, 0, cameraDistance], [0, lookAtY, 0], [0, 1, 0]);
      var model = multiply(
        multiply(rotateY(rotation + tiltInertiaY), rotateX(tiltAngle + tiltInertiaX)),
        translateY(sphereOffsetY)
      );
      var viewModel = multiply(view, model);

      gl.uniformMatrix4fv(uProjection, false, projection);
      gl.uniformMatrix4fv(uView, false, viewModel);
      gl.uniform2f(uPointer, pointer.x, pointer.y);
      gl.uniform2f(uPointerVel, pointerVelDisplay.x * speedCurrent, pointerVelDisplay.y * speedCurrent);
      gl.uniform1f(uHover, hoverCurrent);
      gl.uniform1f(uSpeedFactor, speedCurrent);
      gl.uniform1f(uElastic, elastic);
      gl.uniform1f(uHoverStrength, hoverStrength);
      gl.uniform1f(uBlobFalloff, blobFalloff);
      gl.uniform1f(uPushStrength, pushStrength);
      gl.uniform1f(uPushJitter, pushJitter);
      gl.uniform1f(uSmudgeStrength, smudgeStrength);
      gl.uniform1f(uRippleStrength, rippleStrength);
      gl.uniform1f(uRippleSpeed, rippleSpeed);
      gl.uniform1f(uSwirlStrength, swirlStrength);
      gl.uniform1f(uSwirlSpeed, swirlSpeed);
      gl.uniform1f(uNoiseFreq, noiseFreq);
      gl.uniform1f(uNoiseAmp, noiseAmp);
      gl.uniform1f(uNoiseSpeed, noiseSpeed);
      gl.uniform1f(uTime, animTime);
      gl.uniform1f(uPointScale, pointScale * dpr * 0.34);
      gl.uniform3fv(uColorHi, colorHi);
      gl.uniform3fv(uColorLo, colorLo);
      gl.uniform3fv(uColorAccent, colorAccent);

      gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
      gl.enableVertexAttribArray(aPosition);
      gl.vertexAttribPointer(aPosition, 3, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, seedBuffer);
      gl.enableVertexAttribArray(aSeed);
      gl.vertexAttribPointer(aSeed, 1, gl.FLOAT, false, 0, 0);

      gl.drawArrays(gl.POINTS, 0, count);
    }

    resize();
    rafId = global.requestAnimationFrame(draw);

    return {
      resize: resize,
      destroy: function () {
        running = false;
        if (rafId) global.cancelAnimationFrame(rafId);
        pointerTargets.forEach(function (el) {
          el.removeEventListener("pointermove", onPointerMove);
          el.removeEventListener("pointerenter", onPointerEnter);
          el.removeEventListener("pointerleave", onPointerLeave);
          el.removeEventListener("touchmove", onPointerMove);
        });
        try {
          gl.deleteProgram(program);
          gl.deleteBuffer(posBuffer);
          gl.deleteBuffer(seedBuffer);
        } catch (e) {}
      },
    };
  }

  global.SensNebulaOrb = {
    mount: mountNebulaOrb,
    prefersReducedMotion: prefersReducedMotion,
  };
})(window);
